<template>
  <div id="prices-offer-print" class="quotation-preview" dir="rtl">
    <header class="quotation-toolbar">
      <div class="toolbar-title">
        <strong>معاينة وتصدير عرض السعر الرسمي</strong>
        <span v-if="offerCode" dir="ltr">({{ offerCode }})</span>
      </div>
      <div class="toolbar-actions">
        <button class="print-button" type="button" :disabled="loading || !!error" @click="printOffer">
          <span aria-hidden="true">▣</span> طباعة / حفظ PDF
        </button>
        <button class="close-button" type="button" aria-label="العودة إلى عروض الأسعار" @click="goBack">×</button>
      </div>
    </header>

    <div v-if="loading" class="preview-message" role="status">جاري تحميل عرض السعر...</div>
    <div v-else-if="error" class="preview-message preview-error" role="alert">
      {{ error }}
      <button type="button" @click="load">إعادة المحاولة</button>
    </div>

    <article v-else class="quotation-paper" aria-label="عرض السعر الرسمي">
      <div class="paper-content">
        <header class="paper-header">
          <div class="company-identity">
            <div class="company-mark">
              <img v-if="logoUrl && !logoFailed" :src="logoUrl" alt="شعار الشركة" @error="logoFailed = true" />
              <span v-else aria-hidden="true">ش</span>
            </div>
            <div>
              <h1>{{ company.companyName || 'اسم الشركة غير متوفر' }}</h1>
              <div>
                <span v-if="company.commercialRegister">سجل تجاري: {{ company.commercialRegister}} | </span>
                <span v-if="company.taxRegister">السجل الضريبي: {{ company.taxRegister }} | </span>
                <span v-if="company.taxCardNumber">رقم البطاقة الضريبية: {{ company.taxCardNumber }}</span>
              </div>
                <p v-if="company.telephoneNumber || company.email" class="company-contact">
                <span v-if="company.telephoneNumber" dir="ltr">رقم الهاتف: {{ company.telephoneNumber }}</span>
                <span v-if="company.email" >البريد الالكتروني: {{ company.email }}</span>
              </p>
              <p v-if="company.website" >الموقع الالكتروني: {{ company.website }}</p>
            </div>
          </div>
          <div class="document-meta">
            <strong class="document-number" dir="ltr">{{ offerCode || '—' }}</strong>
            <span>تاريخ الإصدار: <bdi>{{ formatDate(offer.offerDate || offer.createdAt) }}</bdi></span>
            <span>تاريخ انتهاء العرض: <bdi>{{ formatDate(offer.expiryDate) }}</bdi></span>
          </div>
        </header>

        <section class="offer-heading">
          <div>
            <h2>عرض سعر عقاري {{ purposeLabel ? `(${purposeLabel})` : '' }}</h2>
            <p v-if="offer.employeeName || branchName">
              <span v-if="offer.employeeName">الموظف المسؤول: {{ offer.employeeName }}</span>
              <span v-if="offer.employeeName && branchName"> · </span>
              <span v-if="branchName">الفرع: {{ branchName }}</span>
            </p>
          </div>
          <span class="status-pill" :class="`status-${offer.status || 'new'}`">{{ statusLabel }}</span>
        </section>

        <div class="information-grid">
          <section class="information-card">
            <h3>بيانات العميل</h3>
            <p><span>اسم العميل:</span> <strong>{{ offer.clientName || '—' }}</strong></p>
            <p><span>الجوال:</span> <strong dir="ltr">{{ offer.clientMobile || offer.mobile || '—' }}</strong></p>
            <p v-if="offer.clientEmail"><span>البريد:</span> <strong dir="ltr">{{ offer.clientEmail }}</strong></p>
          </section>
          <section class="information-card">
            <h3>تفاصيل العقار</h3>
            <p><span>اسم العقار:</span> <strong>{{ offer.propertyName || '—' }}</strong></p>
            <p><span>رقم العقار / الوحدة:</span> <strong>{{ offer.unitNumber || '—' }}</strong></p>
            <p v-if="offer.deedNumber"><span>رقم الصك:</span> <strong dir="ltr">{{ offer.deedNumber }}</strong></p>
            <p v-if="offer.propertyDescription"><span>وصف العقار:</span> <strong>{{ offer.propertyDescription }}</strong></p>
            <p v-if="Number(offer.pricePerSqm) > 0"><span>المساحة × سعر المتر:</span>
              <strong><bdi>{{ formatArea(offer.areaSqm) }} م² × {{ money(offer.pricePerSqm) }} ر.س/م²</bdi></strong>
            </p>
            <p v-else><span>المساحة:</span> <strong><bdi>{{ formatArea(offer.areaSqm) }} م²</bdi></strong></p>
          </section>
        </div>

        <section class="pricing-section">
          <h3>تفاصيل الحسابات والرسوم المباشرة</h3>
          <table class="pricing-table">
            <thead>
              <tr><th scope="col">البيان / تفاصيل البند</th><th scope="col">الحالة الضريبية</th><th scope="col">المبلغ (ر.س)</th></tr>
            </thead>
            <tbody>
              <tr><td>سعر العقار الأساسي قبل الخصم</td><td>سعر العقار</td><td dir="ltr">{{ money(basePrice) }}</td></tr>
              <tr class="discount-row"><td>خصم من سعر العقار</td><td>خصم</td><td dir="ltr">− {{ money(discountAmount) }}</td></tr>
              <tr class="discounted-price-row"><td>سعر العقار بعد الخصم</td><td>خاضع للضريبة</td><td dir="ltr">{{ money(discountedBasePrice) }}</td></tr>
              <tr><td>{{ commissionLabel }}</td><td>خاضع للضريبة</td><td dir="ltr">{{ money(commissionAmount) }}</td></tr>
              <tr><td>رسوم العقد الإلكتروني (توثيق)</td><td>إداري</td><td dir="ltr">{{ money(offer.contractFee) }}</td></tr>
              <tr><td>رسوم المياه</td><td>إداري</td><td dir="ltr">{{ money(waterFee) }}</td></tr>
              <tr><td>رسوم التأمين المستردة</td><td>تأمين</td><td dir="ltr">{{ money(offer.insuranceFee) }}</td></tr>
              <tr class="vat-row"><td>إجمالي ضريبة القيمة المضافة (VAT 15%)</td><td>تلقائي</td><td dir="ltr">{{ money(vatAmount) }}</td></tr>
              <tr class="total-row"><th scope="row">المبلغ الإجمالي النهائي المطلوب شامل الضريبة</th><td>شامل الكل</td><td dir="ltr">{{ money(totalAmount) }} ر.س</td></tr>
            </tbody>
          </table>
        </section>

        <section v-if="installments.length" class="installments-section">
          <h3>جدول الدفعات المالية</h3>
          <table class="installments-table">
            <thead><tr><th scope="col">الدفعة</th><th scope="col">النسبة</th><th scope="col">المبلغ (ر.س)</th><th scope="col">موعد / شرط الاستحقاق</th></tr></thead>
            <tbody>
              <tr v-for="(installment, index) in installments" :key="installment.id || index">
                <td>{{ installment.label || ('الدفعة ' + (index + 1)) }}</td>
                <td><bdi>{{ installment.percentage === null || installment.percentage === undefined || installment.percentage === '' ? '—' : formatPercentage(installment.percentage) + '%' }}</bdi></td>
                <td dir="ltr">{{ money(installment.amount) }}</td>
                <td>{{ installment.dueCondition || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section v-if="hasBankDetails" class="bank-section">
          <div class="section-heading">
            <h3>بيانات التحويل البنكي الرسمي ودفع العربون</h3>
            <span>{{ offer.bankAccountId ? 'حساب رسمي معتمد' : 'حساب التحويل' }}</span>
          </div>
          <div class="bank-details">
            <div class="bank-grid">
              <p><span>اسم البنك:</span> <strong>{{ bank.bankName || '—' }}</strong></p>
              <p><span>اسم الحساب:</span> <strong>{{ bank.accountName || '—' }}</strong></p>
              <p v-if="bank.accountNumber"><span>رقم الحساب:</span> <strong dir="ltr">{{ bank.accountNumber }}</strong></p>
              <p><span>رقم الآيبان (IBAN):</span> <strong dir="ltr">{{ bank.iban || '—' }}</strong></p>
            </div>
            <div v-if="ibanQrCodeUrl" class="iban-qr-block">
              <img :src="ibanQrCodeUrl" alt="رمز QR يحتوي على رقم الآيبان" />
              <span>امسح الرمز لعرض الآيبان ونسخه</span>
            </div>
          </div>
          <p v-if="offer.paymentNote" class="payment-note">{{ offer.paymentNote }}</p>
        </section>

        <section class="terms-section">
          <h3>الشروط والأحكام العامة:</h3>
          <ul>
            <li v-if="offer.expiryDate">هذا عرض سعر مبدئي وساري حتى تاريخ انتهاء العرض الموضح أعلاه.</li>
            <li v-if="gracePeriodDays">فترة السماح: {{ gracePeriodDays }} {{ gracePeriodDays === 1 ? 'يوم' : 'أيام' }}.</li>
            <li>يعتبر العرض ملغى تلقائياً في حال عدم إرفاق إشعار العربون قبل انتهاء مدة العرض.</li>
          </ul>
        </section>

        <footer class="paper-footer">
          <div class="signature-block">
            <strong>توقيع وختم الشركة</strong>
            <span class="signature-line">ختم المؤسسة</span>
          </div>
          <div class="links-block">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="رمز QR يفتح روابط العقارية" />
            <a href="https://linktr.ee/amaqaria?utm_source=qr_code" target="_blank" rel="noopener noreferrer">روابط شركة أحمد المحيسني العقارية</a>
          </div>
          <div class="signature-block client-signature">
            <strong>موافقة واعتماد العميل</strong>
            <span class="signature-line">التوقيع: ____________</span>
          </div>
        </footer>
      </div>
    </article>
  </div>
</template>

<script>
const numeric = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const snapshot = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
};

const hasContent = value => value && Object.values(value).some(item => item !== null && item !== undefined && item !== '');

export default {
  name: 'PricesOfferPrint',
  data() {
    return {
      offer: {},
      companyDetails: {},
      loading: true,
      error: '',
      copyFeedback: '',
      logoFailed: false,
      previousTitle: '',
    };
  },
  computed: {
    company() {
      const saved = snapshot(this.offer.companySnapshot);
      return hasContent(saved) ? saved : this.companyDetails;
    },
    bank() {
      const saved = snapshot(this.offer.bankSnapshot);
      if (hasContent(saved)) return saved;
      if (this.offer.bankName || this.offer.accountName || this.offer.iban) {
        return {
          bankName: this.offer.bankName,
          accountName: this.offer.accountName,
          iban: this.offer.iban,
          accountNumber: this.offer.accountNumber,
        };
      }
      return (this.companyDetails.bankAccounts || []).find(account => String(account.id) === String(this.offer.bankAccountId)) || {};
    },
    hasBankDetails() {
      return !!(this.bank.bankName || this.bank.accountName || this.bank.iban);
    },
    logoUrl() {
      const logo = this.company.logo;
      if (!logo) return '';
      if (/^(https?:|data:image\/|blob:)/i.test(logo)) return logo;
      return `${this.$store.getters.MEDIA_URL}${String(logo).replace(/^\/?uploads\//, '').replace(/^\//, '')}`;
    },
    offerCode() {
      return this.offer.offerNumber || '';
    },
    purposeLabel() {
      const purpose = this.offer.purpose || this.offer.type;
      return ({ sale: 'بيع', rent: 'تأجير' })[purpose] || purpose || '';
    },
    statusLabel() {
      return ({ new: 'جديد', follow_up: 'قيد المتابعة', accepted: 'مقبول', converted: 'محوّل إلى عقد', expired: 'منتهي', cancelled: 'ملغي' })[this.offer.status] || this.offer.status || 'جديد';
    },
    branchName() {
      if (this.offer.branchName) return this.offer.branchName;
      const branch = (this.companyDetails.branches || []).find(item => String(item.id) === String(this.offer.branchId));
      return branch ? branch.name : '';
    },
    basePrice() {
      return numeric(this.offer.basePrice, numeric(this.offer.areaSqm) * numeric(this.offer.pricePerSqm));
    },
    discountAmount() {
      return numeric(this.offer.discountAmount, Math.max(0, this.basePrice - numeric(this.offer.discountedBasePrice, this.basePrice)));
    },
    discountedBasePrice() {
      return numeric(this.offer.discountedBasePrice, Math.max(0, this.basePrice - this.discountAmount));
    },
    commissionAmount() {
      return numeric(this.offer.commissionAmount, this.offer.commissionMethod === 'fixed'
        ? numeric(this.offer.commissionRate)
        : this.discountedBasePrice * numeric(this.offer.commissionRate) / 100);
    },
    vatAmount() {
      return numeric(this.offer.vatAmount, (this.discountedBasePrice + this.commissionAmount) * 0.15);
    },
    waterFee() {
      return numeric(this.offer.waterFee);
    },
    gracePeriodDays() {
      return numeric(this.offer.gracePeriodDays);
    },
    totalAmount() {
      return numeric(this.offer.totalAmount, this.discountedBasePrice + this.commissionAmount + this.waterFee
        + numeric(this.offer.contractFee) + numeric(this.offer.insuranceFee) + this.vatAmount);
    },
    installments() {
      const value = this.offer.installments;
      let rows = value;
      if (typeof rows === 'string') {
        try {
          rows = JSON.parse(rows);
        } catch (_) {
          return [];
        }
      }
      return Array.isArray(rows) ? rows.filter(row => row && typeof row === 'object' && !Array.isArray(row)) : [];
    },
    commissionLabel() {
      const rate = numeric(this.offer.commissionRate);
      if (this.offer.commissionMethod === 'percent' && rate) return `عمولة الوساطة / السعي (${rate}%)`;
      return 'عمولة الوساطة / السعي';
    },
    qrCodeUrl() {
      const value = this.offer.qrCodeDataUrl;
      return typeof value === 'string' && /^(data:image\/(png|jpeg|svg\+xml);|https?:\/\/)/i.test(value) ? value : '';
    },
    ibanQrCodeUrl() {
      const value = this.offer.ibanQrCodeDataUrl;
      return typeof value === 'string' && /^(data:image\/(png|jpeg|svg\+xml);|https?:\/\/)/i.test(value) ? value : '';
    },
  },
  methods: {
    money(value) {
      return numeric(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatArea(value) {
      return numeric(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
    },
    formatPercentage(value) {
      return numeric(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
    },
    formatDate(value) {
      if (!value) return '—';
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}-${match[2]}-${match[1]}`;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB');
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const id = this.$route.params.id;
        if (!id) throw new Error('missing id');
        const { data } = await this.$http.get(`/api/admin/pricesOffers/retrieve/${encodeURIComponent(id)}`);
        const row = data.row;
        if (!row) throw new Error('missing offer');
        this.offer = row;
        this.copyFeedback = '';
        this.companyDetails = {};
        const needsCompany = !hasContent(snapshot(row.companySnapshot))
          || (!hasContent(snapshot(row.bankSnapshot)) && row.bankAccountId)
          || (!row.branchName && row.branchId);
        if (needsCompany) {
          try {
            const companyResponse = await this.$http.get('/api/admin/companyDetails');
            this.companyDetails = companyResponse.data.data || {};
          } catch (_) {
          }
        }
        this.logoFailed = false;
        document.title = `عرض السعر ${row.offerNumber || id}`;
      } catch (_) {
        this.error = 'تعذر تحميل عرض السعر. يرجى المحاولة مرة أخرى.';
      } finally {
        this.loading = false;
      }
    },
    printOffer() {
      window.print();
    },
    goBack() {
      this.$router.push('/pricesOffers');
    },
  },
  created() {
    this.previousTitle = document.title;
    document.body.classList.add('prices-offer-preview-active');
    this.load();
  },
  beforeUnmount() {
    document.body.classList.remove('prices-offer-preview-active');
    document.title = this.previousTitle;
  },
};
</script>

<style scoped>
.quotation-preview { min-height: 100vh; background: #f4f5f0; color: #283324; font-family: 'Cairo', 'Tajawal', Arial, sans-serif; padding-bottom: 28px; }
.quotation-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 52px; padding: 8px 24px; background: #263a20; color: #fff; }
.toolbar-title { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.toolbar-actions { display: flex; align-items: center; gap: 12px; }
.quotation-toolbar button { border: 0; cursor: pointer; }
.print-button { border-radius: 6px; padding: 7px 13px; background: #d8ca9f; color: #263a20; font-weight: 700; font-size: 12px; }
.print-button:disabled { opacity: .6; cursor: default; }
.close-button { width: 28px; height: 28px; background: transparent; color: #e4ebdc; font-size: 24px; line-height: 1; }
.preview-message { width: min(210mm, calc(100% - 28px)); margin: 24px auto; padding: 20px; background: #fff; border: 1px solid #d4dfcc; text-align: center; }
.preview-error { color: #9d2d2d; }
.preview-error button { margin-inline-start: 12px; border: 1px solid #9d2d2d; border-radius: 4px; background: #fff; color: inherit; padding: 4px 10px; }
.quotation-paper { width: min(210mm, calc(100% - 28px)); min-height: 297mm; margin: 20px auto 0; padding: 14mm 11mm 11mm; box-sizing: border-box; background: #fff; color: #283324; border: 1px solid #cbd8c4; box-shadow: 0 3px 8px #1c191a20; font-size: 11px; line-height: 1.5; }
.paper-content { min-height: 272mm; display: flex; flex-direction: column; }
.paper-header { display: flex; justify-content: space-between; gap: 18px; padding-bottom: 10px; border-bottom: 2px solid #263a20; }
.company-identity { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.company-mark { flex: 0 0 50px; height: 50px; display: grid; place-items: center; border-radius: 8px; background: #263a20; color: #d8ca9f; font-size: 29px; font-weight: 800; }
.company-mark img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.company-identity h1 { margin: 0 0 3px; color: #263a20; font-size: 16px; line-height: 1.3; font-weight: 800; }
.company-identity p { margin: 0; color: #66745f; font-size: 9px; }
.company-contact { display: flex; gap: 8px; flex-wrap: wrap; }
.document-meta { flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-end; gap: 2px; color: #51644c; font-size: 9px; }
.document-number { align-self: flex-end; border: 1px solid #c6d9b8; background: #f4f7f0; border-radius: 3px; padding: 3px 8px; color: #263a20; font-size: 10px; }
.offer-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 11px 0 13px; padding: 8px 10px; background: #f4f7f0; border-right: 3px solid #a69057; }
.offer-heading h2 { margin: 0; font-size: 13px; font-weight: 800; }
.offer-heading p { margin: 2px 0 0; font-size: 9px; color: #697663; }
.status-pill { display: inline-block; flex: 0 0 auto; border-radius: 4px; padding: 3px 8px; background: #ebf4df; color: #3e572e; font-size: 10px; font-weight: 700; }
.status-accepted, .status-converted { background: #e5f0df; color: #385e36; }
.status-follow_up { background: #f5ead0; color: #765d27; }
.status-expired, .status-cancelled { background: #eeeee8; color: #66745f; }
.information-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.information-card { min-width: 0; min-height: 79px; padding: 8px 10px; border: 1px solid #d4dfcc; border-radius: 5px; }
.information-card h3, .pricing-section h3, .terms-section h3 { margin: 0 0 6px; font-size: 10px; font-weight: 800; }
.information-card h3 { border-bottom: 1px solid #e4eadf; padding-bottom: 5px; }
.information-card p { display: flex; gap: 3px; flex-wrap: wrap; margin: 2px 0; }
.information-card p span { color: #66745f; }
.information-card strong { overflow-wrap: anywhere; }
.pricing-section { margin-bottom: 13px; }
.pricing-section h3 { margin-bottom: 5px; }
.pricing-table { width: 100%; border-collapse: collapse; font-size: 10px; }
.pricing-table th, .pricing-table td { padding: 5px 8px; border: 1px solid #dce6d5; }
.pricing-table thead { background: #263a20; color: #fff; }
.pricing-table thead th { border-color: #263a20; font-weight: 700; }
.pricing-table th:first-child, .pricing-table td:first-child { text-align: right; width: 56%; }
.pricing-table th:nth-child(2), .pricing-table td:nth-child(2) { width: 23%; text-align: center; }
.pricing-table th:last-child, .pricing-table td:last-child { text-align: left; white-space: nowrap; }
.pricing-table tbody td:nth-child(2) { color: #66745f; }
.pricing-table .discount-row { background: #fffaf0; color: #765d27; }
.pricing-table .discount-row td:last-child { font-weight: 800; }
.pricing-table .discounted-price-row { background: #f4f7f0; font-weight: 700; }
.pricing-table .vat-row { background: #f4f7f0; font-weight: 700; }
.pricing-table .vat-row td:last-child { color: #765d27; }
.pricing-table .total-row { background: #263a20; color: #fff; font-weight: 800; font-size: 11px; }
.pricing-table .total-row th, .pricing-table .total-row td { border-color: #263a20; }
.installments-section { margin-bottom: 11px; }
.installments-section h3 { margin: 0 0 5px; color: #263a20; font-size: 10px; font-weight: 800; break-after: avoid; }
.installments-table { width: 100%; border-collapse: collapse; font-size: 9px; }
.installments-table th, .installments-table td { border: 1px solid #dce6d5; padding: 5px 7px; }
.installments-table thead { background: #eaf0e5; color: #263a20; }
.installments-table th { font-weight: 800; }
.installments-table th:nth-child(2), .installments-table td:nth-child(2) { width: 13%; text-align: center; white-space: nowrap; }
.installments-table th:nth-child(3), .installments-table td:nth-child(3) { width: 20%; text-align: left; white-space: nowrap; }
.installments-table th:last-child, .installments-table td:last-child { width: 34%; }
.installments-table tbody tr { break-inside: avoid; }
.bank-section { border: 1px solid #c4ae71; border-radius: 7px; padding: 8px 10px; margin-bottom: 11px; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; border-bottom: 1px solid #d8ca9f; padding-bottom: 5px; margin-bottom: 7px; }
.section-heading h3 { margin: 0; color: #6e5829; font-size: 10px; font-weight: 800; }
.section-heading span { background: #f5ead0; color: #765d27; padding: 2px 5px; border-radius: 3px; font-size: 9px; }
.bank-details { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.bank-grid { min-width: 0; flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 3px 10px; }
.bank-grid p { margin: 0; overflow-wrap: anywhere; }
.bank-copy-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; grid-column: 1 / -1; }
.copy-iban-button { border: 1px solid #c4ae71; border-radius: 4px; padding: 3px 8px; background: #f5ead0; color: #5c4c2c; font: inherit; font-size: 9px; font-weight: 700; cursor: pointer; }
.copy-iban-button:hover, .copy-iban-button:focus-visible { background: #e9d9aa; }
.copy-feedback { color: #385e36; font-size: 9px; }
.iban-qr-block { flex: 0 0 75px; display: flex; flex-direction: column; align-items: center; gap: 2px; color: #5c4c2c; font-size: 8px; text-align: center; }
.iban-qr-block img { width: 60px; height: 60px; object-fit: contain; }
.bank-grid span { color: #697663; }
.bank-grid strong { font-weight: 700; }
.payment-note { margin: 7px 0 0; padding: 5px 7px; border: 1px solid #d8ca9f; border-radius: 3px; color: #5c4c2c; font-size: 9px; }
.terms-section { border: 1px solid #d4dfcc; border-radius: 5px; padding: 8px 10px; margin-bottom: 11px; }
.terms-section h3 { margin-bottom: 3px; }
.terms-section ul { margin: 0; padding-inline-start: 16px; color: #66745f; }
.paper-footer { display: flex; justify-content: space-between; align-items: flex-end; gap: 10px; margin-top: auto; padding-top: 10px; border-top: 1px solid #dce6d5; }
.signature-block { width: 29%; display: flex; flex-direction: column; gap: 20px; min-height: 54px; font-size: 9px; }
.signature-line { align-self: flex-start; color: #9aa991; border: 1px dashed #cbd8c4; border-radius: 3px; padding: 4px 8px; }
.client-signature { align-items: flex-start; }
.client-signature .signature-line { border: 0; border-bottom: 1px solid #9aa991; border-radius: 0; }
.links-block { min-width: 90px; display: flex; align-items: center; flex-direction: column; gap: 3px; color: #66745f; font-size: 8px; }
.links-block img { width: 50px; height: 50px; object-fit: contain; }
.links-block a { color: #51644c; text-decoration: none; }
@media (max-width: 620px) {
  .quotation-toolbar { padding: 8px 10px; }
  .toolbar-title { font-size: 11px; }
  .print-button { font-size: 10px; padding: 6px; }
  .quotation-paper { padding: 16px 12px; min-height: auto; }
  .paper-content { min-height: 0; }
  .paper-header { flex-wrap: wrap; }
  .document-meta { align-items: flex-start; }
  .document-number { align-self: flex-start; }
  .information-grid { grid-template-columns: 1fr; }
  .pricing-table { font-size: 8px; }
  .pricing-table th, .pricing-table td { padding: 4px 3px; }
  .bank-grid { grid-template-columns: 1fr; }
  .bank-details { align-items: flex-start; }
  .installments-table { font-size: 8px; }
  .installments-table th, .installments-table td { padding: 4px 3px; }
  .paper-footer { margin-top: 25px; }
}
</style>

<style>
body.prices-offer-preview-active .app-wrapper { display: block !important; min-height: 100vh; }
body.prices-offer-preview-active .app-wrapper > :not(#prices-offer-print) { display: none !important; }
@media screen {
  html[data-bs-theme="dark"] #prices-offer-print { background: #10140f; }
  html[data-bs-theme="dark"] #prices-offer-print .quotation-toolbar {
    border-bottom: 1px solid rgba(166,144,87,.18);
    background: #1a2118;
  }
  html[data-bs-theme="dark"] #prices-offer-print .preview-message {
    border-color: #3b4936;
    color: #e4eae0;
    background: #1a2118;
  }
  html[data-bs-theme="dark"] #prices-offer-print .preview-error { color: #f1a49e; }
  html[data-bs-theme="dark"] #prices-offer-print .preview-error button { color: #f1a49e; background: #38241f; border-color: #774a40; }
  html[data-bs-theme="dark"] #prices-offer-print .quotation-paper { box-shadow: 0 16px 40px rgba(0,0,0,.5); }
}
@page { size: A4; margin: 8mm; }
@media print {
  html, body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body.prices-offer-preview-active .app-wrapper { min-height: 0 !important; }
  #prices-offer-print { width: 100% !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; background: #fff !important; }
  #prices-offer-print .quotation-toolbar, #prices-offer-print .preview-message, #prices-offer-print .bank-copy-controls { display: none !important; }
  #prices-offer-print .quotation-paper { width: 100% !important; min-height: 281mm !important; margin: 0 !important; padding: 6mm 5mm !important; border: 0 !important; box-shadow: none !important; }
  #prices-offer-print .paper-content { min-height: 269mm !important; }
  #prices-offer-print .paper-header, #prices-offer-print .information-grid, #prices-offer-print .bank-section, #prices-offer-print .terms-section, #prices-offer-print .paper-footer { break-inside: avoid; }
  #prices-offer-print .pricing-table tr, #prices-offer-print .installments-table tr { break-inside: avoid; }
}
</style>
